import { Component, Accents, Story, Knobs, TextTypes } from './DocTypes';

const PlaygroundStoryKnobs: Knobs = {
  text: { default: 'Some interesting text...', editor: 'textbox' },
  type: {
    default: '',
    editor: 'dropdown',
    editorOptions: [''].concat(TextTypes),
  },
  accent: {
    default: '',
    editor: 'dropdown',
    editorOptions: [''].concat(Accents),
  },
  'colour-scheme': {
    editor: 'dropdown',
    editorOptions: ['light', 'dark', 'default', ''],
    default: '',
  },
  device: {
    editor: 'dropdown',
    editorOptions: ['mobile', 'desktop', ''],
    default: '',
  },
};
const playgroundStory: Story = {
  title: 'Playground',
  description: 'A simple text element with various attributes',
  coreComponent: {
    html: `<tag-text {{text}} {{type}} {{accent}}></tag-text>`,
    knobs: PlaygroundStoryKnobs,
  },
  coreComponent_v3: {
    html: `<tag3-text {{...knobs}}></tag3-text>`,
    knobs: PlaygroundStoryKnobs,
  },
  reactWrapper: { jsx: `<TagText {{text}} {{type}} {{accent}} />`, knobs: PlaygroundStoryKnobs },
  reactWrapper_v3: {
    jsx: `<Tag3Text {{...knobs}}/>`,
    knobs: PlaygroundStoryKnobs,
  },
};

const NumberFormattingKnobs: Knobs = {
  text: { default: '54038.1000000000', editor: 'number' },
  formattingOptions: {
    attribute: 'formatting-options',
    default: '{"style": "currency","currency":"GBP"}',
    editor: 'multilinetextbox',
  },
};
const numberFormattingStory: Story = {
  title: 'Number formatting',
  description: 'You can format numbers using the `formatting-options` attribute',
  coreComponent: {
    html: `<tag-text type='feature' data-type='decimal' {{text}}{{formattingOptions}}></tag-text>`,
    knobs: NumberFormattingKnobs,
  },
  coreComponent_v3: {
    html: `<tag3-text type='feature' data-type='decimal' {{text}}{{formattingOptions}}></tag3-text>`,
    knobs: NumberFormattingKnobs,
  },
  reactWrapper: {
    jsx: `<TagText type="feature" dataType='decimal' {{text}}{{formattingOptions}}/>`,
    knobs: NumberFormattingKnobs,
  },
  reactWrapper_v3: {
    jsx: `<Tag3Text type="feature" dataType='decimal'{{...knobs}}/>`,
    knobs: NumberFormattingKnobs,
  },
};

const DateFormattingKnobs: Knobs = {
  text: { default: '2019-12-25T10:20:00.057Z', editor: 'textbox' },
  dataType: {
    default: 'date',
    editor: 'dropdown',
    editorOptions: ['date', 'datetime'],
  },
};
const dateFormattingStory: Story = {
  title: 'Date formatting',
  description: 'Dates can automatically be formatted by specifying the correct data-type',
  coreComponent: {
    html: `<tag-text type='feature' {{text}}{{dataType}}></tag-text>`,
    knobs: DateFormattingKnobs,
  },
  coreComponent_v3: {
    html: `<tag3-text type='feature' {{dataType}} {{text}}></tag3-text>`,
    knobs: DateFormattingKnobs,
  },
  reactWrapper: {
    jsx: `<TagText type="feature" {{dataType}} {{text}} />`,
    knobs: DateFormattingKnobs,
  },
  reactWrapper_v3: {
    jsx: `<Tag3Text type="feature"{{dataType}} {{text}}/>`,
    knobs: DateFormattingKnobs,
  },
};

const customStylesStory: Story = {
  title: 'Custom styles',
  description:
    'You can style the text element by passing a JSON object containing CSS properties to the `text-style` attribute',
  coreComponent: {
    html: `<tag-text {{text}}{{textStyle}}{{type}}></tag-text>`,
    knobs: {
      text: { default: 'Some text with custom styles', editor: 'textbox' },
      type: {
        default: '',
        editor: 'dropdown',
        editorOptions: [''].concat(TextTypes),
      },
      textStyle: {
        attribute: 'text-style',
        default: '{"font-size": "44px"}',
        editor: 'multilinetextbox',
      },
    },
  },
  coreComponent_v3: {
    html: `<tag3-text {{text}}{{textStyle}}{{type}}></tag3-text>`,
    knobs: {
      text: { default: 'Some text with custom styles', editor: 'textbox' },
      type: {
        default: '',
        editor: 'dropdown',
        editorOptions: [''].concat(TextTypes),
      },
      textStyle: {
        attribute: 'text-style',
        default: '{"font-size": "44px"}',
        editor: 'multilinetextbox',
      },
    },
  },
  reactWrapper: {
    jsx: `<TagText {{text}}{{textStyle}}{{type}} />`,
    knobs: {
      text: { default: 'Some text with custom styles', editor: 'textbox' },
      type: {
        default: '',
        editor: 'dropdown',
        editorOptions: [''].concat(TextTypes),
      },
      textStyle: {
        default: '{fontSize: "44px"}',
        editor: 'multilinetextbox',
        qualifierStart: '{',
        qualifierEnd: '}',
      },
    },
  },
  reactWrapper_v3: {
    jsx: `<Tag3Text {{text}}{{textStyle}}{{type}} />`,
    knobs: {
      text: { default: 'Some text with custom styles', editor: 'textbox' },
      type: {
        default: '',
        editor: 'dropdown',
        editorOptions: [''].concat(TextTypes),
      },
      textStyle: {
        default: '{fontSize: "44px"}',
        editor: 'multilinetextbox',
        qualifierStart: '{',
        qualifierEnd: '}',
      },
    },
  },
};

export const TagTextDocs: Component = {
  type: 'component',
  name: 'text',
  description: 'Renders a text element',
  mobileFriendly: true,
  preRelease: '',
  popular: true,
  props: [
    {
      name: 'type',
      description: 'The text type',
      type: `'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'feature' | 'kpi | 'kpi-label' | 'small' | 'p' | 'span' | 'label' | 'edit-field-label'  | 'default' | 'min'`,
    },
    {
      name: 'text',
      description: 'The text',
      type: 'string',
    },
    {
      name: 'inline',
      description: 'Treat text as inline',
      type: 'boolean',
    },
    {
      name: 'accent',
      description: 'The colour of the text',
      type: 'string',
    },
    {
      name: 'textStyle',
      description: 'JSX style object in JSON to apply to the text',
      type: 'string',
    },
    {
      name: 'dataType',
      default: 'string',
      description: 'The type of the field that is used for value formatting',
      type:
        'string | integer | decimal | currency | date | datetime | boolean | image | email | phonenumber',
    },
    {
      name: 'formattingOptions',
      description:
        'Additional formatting options for numbers. This maps directly to the options parameter in Intl.NumberFormat see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/NumberFormat',
      type: 'string',
    },
    {
      name: 'colour-scheme',
      description:
        'V3 Only: Dark mode (theme) control. Default for normal browser driven control. Dark | Light to force respective styling regardless of browser setting.',
      type: 'dark | light | default',
      default: 'light',
    },
    {
      name: 'device',
      description: 'V3 Only: set preferred styling theme for device type.',
      type: 'desktop | mobile',
      default: 'desktop',
    },
  ],
  reactPropOverrides: [],
  methods: [],
  events: [],
  stories: [playgroundStory, numberFormattingStory, dateFormattingStory, customStylesStory],
};
